import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Box, 
  Divider, 
  Fab, 
  Button, 
  Tooltip, 
  useMediaQuery,
  useTheme,
  Chip,
  Paper,
  Fade
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import GavelIcon from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LanguageIcon from '@mui/icons-material/Language';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const serviceCategories = [
  {
    title: 'Business & Company Formation',
    icon: BusinessIcon,
    color: '#1565C0',
    services: [
      'Private Limited Company Registration',
      'One Person Company (OPC) Registration',
      'Limited Liability Partnership (LLP) Formation',
      'Partnership Firm Registration',
      'Sole Proprietorship Registration',
      'Section 8 Company (NGO) Registration',
      'Nidhi Company Registration',
      'Producer Company Registration',
      'Start-up India Registration & DPIIT Recognition'
    ]
  },
  {
    title: 'Taxation & Compliance Services',
    icon: ReceiptIcon,
    color: '#2E7D32',
    services: [
      'GST Registration',
      'GST Return Filing (Monthly, Quarterly, Annual)',
      'Income Tax Return Filing (ITR)',
      'TDS Return Filing',
      'PAN & TAN Application',
      'Advance Tax & Tax Planning',
      'Professional Tax Registration & Filing'
    ]
  },
  {
    title: 'Licensing & Certifications',
    icon: CardMembershipIcon,
    color: '#C62828',
    services: [
      'FSSAI (Food License) Registration',
      'MSME (Udyam) Registration',
      'Import Export Code (IEC)',
      'ISO Certification',
      'Digital Signature Certificate (DSC)',
      'Shops & Establishment License',
      'Trade License'
    ]
  },
  {
    title: 'Intellectual Property Rights',
    icon: GavelIcon,
    color: '#6A1B9A',
    services: [
      'Trademark Registration',
      'Trademark Renewal & Objection Handling',
      'Copyright Registration',
      'Patent Consultation',
      'Design Registration'
    ]
  },
  {
    title: 'Accounting & Bookkeeping',
    icon: AccountBalanceIcon,
    color: '#00695C',
    services: [
      'Monthly Bookkeeping Services',
      'Accounting Software Setup (Tally, Zoho, QuickBooks)',
      'MIS Reports & Financial Statements',
      'Cash Flow & Budgeting'
    ]
  },
  {
    title: 'ROC Compliance & Legal Filings',
    icon: DescriptionIcon,
    color: '#EF6C00',
    services: [
      'Annual Filing of Companies & LLPs',
      'Director KYC (DIR-3 KYC)',
      'Board Resolutions & Minutes Drafting',
      'Change in Company Structure (Name, Address, Directors)',
      'Share Transfer & Allotment',
      'Company Strike-Off & Closure Services'
    ]
  },
  {
    title: 'Audit & Assurance',
    icon: AssessmentIcon,
    color: '#0277BD',
    services: [
      'Internal Audit',
      'Statutory Audit Coordination',
      'GST Audit',
      'Tax Audit'
    ]
  },
  {
    title: 'Business Advisory & Support',
    icon: TrendingUpIcon,
    color: '#283593',
    services: [
      'Business Plan & Project Report Preparation',
      'Investment & Loan Consulting (Mudra, MSME Loans)',
      'Valuation Services',
      'Business Restructuring',
      'Virtual CFO Services'
    ]
  },
  {
    title: 'Website & Branding Solutions',
    icon: LanguageIcon,
    color: '#AD1457',
    services: [
      'Business Email & Domain Registration',
      'Basic Website Development',
      'Logo & Company Branding Services'
    ]
  }
];

const Services = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const handleWhatsAppClick = (serviceTitle) => {
    // Replace this with your actual WhatsApp business number
    const phoneNumber = '+919455133862';
    const message = `Hi! I would like to know more about your ${serviceTitle} services.`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <Box sx={{ 
      pt: { xs: 10, md: 12 }, 
      pb: { xs: 8, md: 10 }, 
      bgcolor: 'background.default', 
      position: 'relative',
      overflowX: 'hidden'
    }}>
      {/* Background elements for visual interest */}
      <Box
        sx={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(25,118,210,0.05) 0%, rgba(25,118,210,0) 70%)',
          top: '5%',
          left: '-10%',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(156,39,176,0.05) 0%, rgba(156,39,176,0) 70%)',
          bottom: '10%',
          right: '-5%',
          zIndex: 0,
        }}
      />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Page Header with improved typography and spacing */}
        <Fade in={true} timeout={1000}>
          <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: 'center' }}>
            <Typography 
              variant="overline" 
              component="div"
              sx={{ 
                color: 'secondary.main', 
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 1,
                fontSize: { xs: '0.85rem', md: '0.95rem' }
              }}
            >
              COMPREHENSIVE LEGAL SOLUTIONS
            </Typography>
            <Typography 
              variant="h2" 
              component="h1" 
              className="font-heading"
              sx={{ 
                fontWeight: 800,
                color: 'primary.main',
                mb: 2.5,
                fontSize: { xs: '2.2rem', md: '3rem' },
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  height: '4px',
                  width: '80px',
                  backgroundColor: 'secondary.main',
                  bottom: '-12px',
                  left: 'calc(50% - 40px)',
                  borderRadius: '2px'
                }
              }}
            >
              Our Professional Services
            </Typography>
            <Typography 
              variant="h6" 
              align="center" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '800px',
                mx: 'auto',
                mt: 4,
                mb: 1,
                fontSize: { xs: '1rem', md: '1.2rem' },
                fontWeight: 400,
                lineHeight: 1.6
              }}
            >
              Expert legal and business solutions tailored to help your business thrive
            </Typography>
            <Typography 
              variant="body1" 
              align="center" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '700px',
                mx: 'auto',
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            >
              From business formation to compliance management, our comprehensive services cover all your legal needs
            </Typography>
          </Box>
        </Fade>
        
        <Grid container spacing={isMobile ? 3 : 4}>
          {serviceCategories.map((category, index) => {
            const Icon = category.icon;
            return (
              <Grid item key={index} xs={12} sm={6} md={4}>
                <Fade 
                  in={true} 
                  style={{ transitionDelay: `${index * 100}ms` }}
                  timeout={800}
                >
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                      transition: 'all 0.4s ease',
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 28px rgba(0,0,0,0.12)'
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '5px',
                        backgroundColor: category.color || 'primary.main',
                      }
                    }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <CardContent sx={{ 
                      p: { xs: 2.5, md: 3.5 }, 
                      flexGrow: 1, 
                      display: 'flex', 
                      flexDirection: 'column' 
                    }}>
                      {/* Service Category Header */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        mb: 3
                      }}>
                        <Box sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: `${category.color}15` || 'primary.light',
                          mr: 2.5,
                          transition: 'all 0.3s ease',
                          ...(hoveredIndex === index && {
                            backgroundColor: category.color || 'primary.main',
                            transform: 'scale(1.05)'
                          })
                        }}>
                          <Icon sx={{ 
                            fontSize: 30, 
                            color: hoveredIndex === index ? '#fff' : category.color || 'primary.main',
                            transition: 'all 0.3s ease',
                          }} />
                        </Box>
                        <Box>
                          <Typography 
                            variant="h5" 
                            component="h2"
                            className="font-heading"
                            sx={{ 
                              fontWeight: 700,
                              color: 'text.primary',
                              fontSize: { xs: '1.2rem', md: '1.35rem' },
                              lineHeight: 1.3,
                              mb: 0.5
                            }}
                          >
                            {category.title}
                          </Typography>
                          <Chip 
                            label={`${category.services.length} services`} 
                            size="small"
                            sx={{ 
                              fontSize: '0.7rem', 
                              height: 22,
                              bgcolor: `${category.color}10` || 'primary.light',
                              color: category.color || 'primary.main',
                              fontWeight: 600
                            }} 
                          />
                        </Box>
                      </Box>
                      
                      <Divider sx={{ mb: 2.5 }} />
                      
                      {/* Service List */}
                      <Box component="ul" sx={{ 
                        listStyle: 'none',
                        p: 0,
                        m: 0,
                        mb: 3
                      }}>
                        {category.services.map((service, idx) => (
                          <Box
                            component="li"
                            key={idx}
                            sx={{
                              mb: 1.5,
                              display: 'flex',
                              alignItems: 'flex-start',
                              '&:last-child': {
                                mb: 0
                              }
                            }}
                          >
                            <Box 
                              component="span" 
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: `${category.color}15` || 'primary.light',
                                color: category.color || 'primary.main',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                mr: 1.5,
                                mt: 0.2,
                                flexShrink: 0
                              }}
                            >
                              ✓
                            </Box>
                            <Typography
                              className="font-sans"
                              sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '0.9rem', md: '0.92rem' },
                                lineHeight: 1.5,
                                fontWeight: 400
                              }}
                            >
                              {service}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      
                      {/* Action Buttons */}
                      <Box sx={{ 
                        mt: 'auto', 
                        pt: 2.5,
                        borderTop: '1px solid',
                        borderColor: 'grey.100',
                        display: 'flex',
                        justifyContent: 'center'
                      }}>
                        <Button
                          variant="contained"
                          startIcon={<WhatsAppIcon />}
                          onClick={() => handleWhatsAppClick(category.title)}
                          sx={{
                            bgcolor: '#25D366',
                            py: 1,
                            px: 2,
                            borderRadius: 1.5,
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            textTransform: 'none',
                            boxShadow: '0 2px 8px rgba(37, 211, 102, 0.2)',
                            '&:hover': {
                              bgcolor: '#128C7E',
                              boxShadow: '0 4px 10px rgba(37, 211, 102, 0.25)',
                            },
                          }}
                        >
                          Get Free Consultation
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            );
          })}
        </Grid>
        
        {/* Call To Action Section */}
        <Fade in={true} timeout={1000} style={{ transitionDelay: '300ms' }}>
          <Paper 
            elevation={0} 
            sx={{ 
              mt: { xs: 6, md: 8 },
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 3,
              background: 'linear-gradient(90deg, rgba(21,101,192,0.04) 0%, rgba(156,39,176,0.04) 100%)',
              border: '1px solid rgba(0,0,0,0.06)',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: 3, md: 4 }
            }}
          >
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography 
                variant="h4" 
                component="h2" 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: { xs: '1.5rem', md: '1.8rem' },
                  mb: 1.5,
                  color: 'primary.dark'
                }}
              >
                Need a Customized Solution?
              </Typography>
              <Typography 
                variant="body1"
                sx={{ 
                  color: 'text.secondary',
                  maxWidth: '600px'
                }}
              >
                Contact our team for personalized legal and business services tailored to your specific requirements.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="secondary"
              size={isMobile ? "medium" : "large"}
              startIcon={<WhatsAppIcon />}
              onClick={() => handleWhatsAppClick('Custom Business Solutions')}
              sx={{
                whiteSpace: 'nowrap',
                py: { xs: 1.2, md: 1.5 },
                px: { xs: 3, md: 4 },
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: { xs: '0.9rem', md: '1rem' },
                boxShadow: '0 4px 12px rgba(156,39,176,0.25)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(156,39,176,0.35)',
                }
              }}
            >
              Schedule a Consultation
            </Button>
          </Paper>
        </Fade>
      </Container>

      {/* Floating WhatsApp Button */}
      <Tooltip title="Chat with us on WhatsApp" placement="left">
        <Fab
          color="secondary"
          aria-label="whatsapp"
          onClick={() => handleWhatsAppClick('your services')}
          sx={{
            position: 'fixed',
            bottom: { xs: 24, md: 32 },
            right: { xs: 24, md: 32 },
            bgcolor: '#25D366',
            boxShadow: '0 4px 12px rgba(37, 211, 102, 0.5)',
            zIndex: 1000,
            '&:hover': {
              bgcolor: '#128C7E',
            },
          }}
        >
          <WhatsAppIcon />
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default Services;